// components/MyPage/index.js
import { useState, useEffect, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { Container, Content, LoadingIndicator } from "./styles";

// 컴포넌트 가져오기
import Sidebar from "./Sidebar";
import Dashboard from "./Dashboard";
import ProfileSettings from "./ProfileSettings";
import SecuritySettings from "./SecuritySettings";
import NotificationSettings from "./NotificationSettings";
import AppearanceSettings from "./AppearanceSettings";

export default function MyPage() {
  const { user, logout,updateUser } = useAuth();
  const [activeSection, setActiveSection] = useState("profile");
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    bio: "",
    avatar_url: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
    emailNotifications: true,
    taskReminders: true,
    darkMode: false,
    defaultTaskColor: "#4EA8DE",
    timeFormat: "24h",
    dateFormat: "yyyy-MM-dd",
  });
  
  const fileInputRef = useRef(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const navigate = useNavigate();

  // 사용자 데이터 로드
  useEffect(() => {
    if (user) {
      setFormData({
        ...formData,
        username: user.username || user.userId || "",
        email: user.email || "",
        bio: user.bio || "",
        avatar_url: user.avatar_url || user.profileImage || "",
        emailNotifications: user.emailNotifications || true,
        taskReminders: user.taskReminders || true,
        darkMode: user.darkMode || false,
        defaultTaskColor: user.defaultTaskColor || "#4EA8DE",
        timeFormat: user.timeFormat || "24h",
        dateFormat: user.dateFormat || "yyyy-MM-dd",
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleAvatarClick = () => {
    if (fileInputRef && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleColorSelect = (color) => {
    setFormData({
      ...formData,
      defaultTaskColor: color
    });
  };

// components/MyPage/index.js의 handleSubmit 함수 수정
const handleSubmit = async (e) => {
  e.preventDefault();
  setLoading(true);
  
  try {
    // 프로필 이미지 업로드 처리
    let updatedAvatarUrl = formData.avatar_url;
    
    if (avatarFile) {
      console.log('파일 업로드 시작:', avatarFile.name);
      
      // FormData 생성 및 파일 추가
      const uploadFormData = new FormData();
      uploadFormData.append('files', avatarFile); // 기존대로 'files' 필드명 사용
      
      try {
        // AWS 파일 서버에 업로드
        const uploadResponse = await fetch('https://file.skynet.re.kr/awsUpload/files', {
          method: 'POST',
          body: uploadFormData,
        });
        
        if (!uploadResponse.ok) {
          throw new Error(`업로드 실패: ${uploadResponse.status} ${uploadResponse.statusText}`);
        }
        
        const uploadData = await uploadResponse.json();
        console.log("프로필 이미지 업로드 응답:", uploadData);
        
        if (uploadData.message === "Multiple files uploaded successfully" && 
            uploadData.files && 
            uploadData.files.length > 0) {
          
          updatedAvatarUrl = uploadData.files[0].url;
          console.log('새 아바타 URL:', updatedAvatarUrl);
        } else {
          throw new Error("이미지 업로드 응답 형식이 예상과 다릅니다.");
        }
      } catch (uploadError) {
        console.error("이미지 업로드 오류:", uploadError);
        toast.error(`프로필 이미지 업로드 실패: ${uploadError.message}`);
        setLoading(false);
        return;
      }
    }
    
    // 프로필 정보 업데이트 API 호출
    const response = await fetch("/request/update_profile", {
      method: "PUT",
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_name: formData.username,
        email_address: formData.email,
        user_alias: formData.bio,
        profile_image: updatedAvatarUrl  // 여기서 AWS에서 반환된 전체 URL을 사용
      }),
      credentials: "include",
    });

    const data = await response.json();

    if (data.success) {
      toast.success("프로필이 성공적으로 업데이트되었습니다.");
      
      // AuthContext에서 가져온 updateUser 함수 사용
      if (typeof updateUser === 'function') {
        updateUser({
          ...user,
          username: formData.username,
          email: formData.email,
          bio: formData.bio,
          profileImage: updatedAvatarUrl
        });
      }
    } else {
      toast.error(data.message || "프로필 업데이트에 실패했습니다.");
    }
  } catch (error) {
    console.error("프로필 업데이트 오류:", error);
    toast.error("서버 연결 중 오류가 발생했습니다.");
  } finally {
    setLoading(false);
  }
};


  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('새 비밀번호와 비밀번호 확인이 일치하지 않습니다.');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch('/request/change-password', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success('비밀번호가 변경되었습니다.');
        setFormData({
          ...formData,
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        toast.error(result.message || '비밀번호 변경에 실패했습니다.');
      }
    } catch (error) {
      console.error('비밀번호 변경 오류:', error);
      toast.error('비밀번호 변경 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login'); // 로그아웃 후 로그인 페이지로 리다이렉트
    } catch (error) {
      toast.error('로그아웃 중 오류가 발생했습니다.');
    }
  };

  const renderContent = () => {
    if (loading) {
      return <LoadingIndicator>로딩 중...</LoadingIndicator>;
    }
    
    switch (activeSection) {
      case "dashboard":
        return <Dashboard />;
      case "profile":
        return (
          <ProfileSettings 
            formData={formData}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            fileInputRef={fileInputRef}
            avatarPreview={avatarPreview}
            handleAvatarClick={handleAvatarClick}
            handleFileChange={handleFileChange}
          />
        );
      case "security":
        return (
          <SecuritySettings 
            formData={formData}
            handleChange={handleChange}
            handlePasswordChange={handlePasswordChange}
          />
        );
      case "notifications":
        return (
          <NotificationSettings 
            formData={formData}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
          />
        );
      case "appearance":
        return (
          <AppearanceSettings 
            formData={formData}
            handleChange={handleChange}
            handleColorSelect={handleColorSelect}
            handleSubmit={handleSubmit}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Container>
      <Sidebar 
        activeSection={activeSection}
        setActiveSection={setActiveSection}
        formData={formData}
        fileInputRef={fileInputRef}
        avatarPreview={avatarPreview}
        handleAvatarClick={handleAvatarClick}
        handleFileChange={handleFileChange}
        handleLogout={handleLogout}
      />
      <Content>
        {renderContent()}
      </Content>
    </Container>
  );
}